import { useCallback, useEffect, useState } from "react"
import { usePreferences } from "hooks/use-preferences"
import { useAppDispatch, useAppSelector } from "hooks/redux"
import { useDashboardId } from "../context/dashboard-id"
import { optimizationSlice, DEFAULT_OPTIMIZATION_STATE } from "../store/optimization-slice"

const PREFERENCE_SUBTYPE = "optimization"
// Optimization fields used to persist under core's "dashboard" subtype; read it once as a
// fallback so existing users' saved settings migrate into the new "optimization" subtype.
const LEGACY_CORE_SUBTYPE = "dashboard"
const PREFERENCE_FIELDS = [
  "isOptimizePanelExpanded",
  "optimizeConfiguration",
  "previewOptimizeConfiguration",
]

/**
 * Syncs the optimization slice with user preferences via usePreferences.
 * Returns true once preferences have been loaded.
 *
 * optimizationExperimentSource and optimizationSourceKind are intentionally
 * omitted: they are applied from the backend via setPreferences but are not
 * actively two-way synced (consistent with prior coreSlice behavior).
 */
export function useSyncOptimizationPreferences(): boolean {
  const dashboardId = useDashboardId()
  const dispatch = useAppDispatch()
  const state = useAppSelector((s) => s.dashboard.optimization[dashboardId] ?? DEFAULT_OPTIMIZATION_STATE)

  // Returning to a dashboard remounts this subtree (key={dashboardId}) and re-runs the one-shot
  // load. When the slice slot was already populated at mount it holds the live value — possibly an
  // edit the debounced save hadn't flushed — so skip the load (including the legacy fallback,
  // already migrated on first load) rather than clobber it with the older cached read.
  const [wasInitializedAtMount] = useState(() => state.isPreferencesLoaded)

  const setPreferencesAction = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (prefs: Record<string, any>) =>
      optimizationSlice.actions.setPreferences({ dashboardId, ...prefs }),
    [dashboardId],
  )

  const isLoaded = usePreferences(
    PREFERENCE_SUBTYPE,
    PREFERENCE_FIELDS,
    state,
    dispatch,
    setPreferencesAction,
    { fallbackSubType: LEGACY_CORE_SUBTYPE, skipInitialLoad: wasInitializedAtMount },
  )

  // When the user has no saved preferences, usePreferences sets isLoaded=true without
  // dispatching setPreferencesAction (because preferencesToApply is {}). This leaves
  // s.dashboard.optimization[dashboardId] undefined → isPreferencesLoaded stays false forever.
  useEffect(() => {
    if (isLoaded && !state.isPreferencesLoaded) {
      dispatch(optimizationSlice.actions.setPreferences({ dashboardId }))
    }
  }, [isLoaded, state.isPreferencesLoaded, dashboardId, dispatch])

  // Return the slice flag, not usePreferences' internal flag, so the gate stays closed until
  // s.dashboard.optimization[dashboardId] is populated (playbook rule #1).
  return state.isPreferencesLoaded
}
